/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineBriefcase, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { useAgentContext } from "../../context/AgentContext";
import { pushOneAgent } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type Agent = {
  dsa_agents_id: number | string;
  name?: string;
  email?: string;
  phone?: string;
  thumbnail?: string;
  [key: string]: unknown;
};

const addRoute = "/agent/add";
const editRoute = "/agent/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { agent, count, fetchAgents, deleteAgent, updateAgentState } =
    useAgentContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!agent) fetchAgents(page, pageSize);
  }, [agent]);

  const rows = useMemo<Agent[] | null>(() => {
    if (!Array.isArray(agent)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return agent as Agent[];
    return (agent as Agent[]).filter((a) =>
      String(a.name ?? "").toLowerCase().includes(q),
    );
  }, [agent, query]);

  const columns: Column<Agent>[] = [
    {
      header: "Name",
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={row.name ?? ""}
              className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-200"
            />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/10 text-[11px] font-semibold text-brand ring-1 ring-inset ring-brand/15">
              {(row.name ?? "AG").slice(0, 2).toUpperCase()}
            </span>
          )}
          <div>
            <p className="font-medium text-gray-900">{row.name ?? "—"}</p>
            {row.email ? (
              <p className="truncate text-[11.5px] text-gray-500">{row.email}</p>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      header: "Phone",
      cell: (row) => (
        <span className="text-gray-600">{row.phone ?? "—"}</span>
      ),
    },
  ];

  const handleDelete = async (row: Agent) => {
    const response: any = await deleteAgent(row.dsa_agents_id);
    if (response?.status === 200 && Array.isArray(agent)) {
      updateAgentState(
        (agent as Agent[]).filter(
          (a) => a.dsa_agents_id !== row.dsa_agents_id,
        ),
      );
    }
  };

  const rowActions = (row: Agent): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOneAgent(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Remove ${row.name ?? "this agent"}?`,
        description: "This agent will be removed from the platform.",
        confirmLabel: "Remove",
      },
    },
  ];

  return (
    <ListPage<Agent>
      title="Agents"
      subtitle="DoSee field agents listed on the platform."
      icon={<HiOutlineBriefcase className="h-5 w-5" />}
      actions={[{ label: "Add agent", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.dsa_agents_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search agents…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchAgents(p, ps);
        },
      }}
      empty="No agents yet."
    />
  );
};

export default Overview;
